import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, LogOut, User, Calendar, X, MapPin, Clock, Users } from 'lucide-react';
import { auth } from '../../config/firebaseConfig';
import * as googleCalendarService from '../../services/googleCalendarService';
import EventDetailModal, { MapModal } from './EventDetailModal';
import BottomNavigation from './BottomNavigation';
import { useToast } from '../../contexts/ToastContext';
import './styles/MobileScheduleView.css';
import { API_BASE_URL } from '../../config/apiConfig';

/**
 * EventCard - 일정 카드 컴포넌트
 */
const EventCard = ({ event, onClick, isToday, isHighlighted, cardRef }) => {
   // 🆕 확정 조건: isCoordinated이고 참석자 2명 이상
   const isConfirmed = event.isCoordinated && event.participants >= 2;

   return (
      <div
         ref={cardRef}
         className={`event-card ${isConfirmed ? 'confirmed' : event.isCoordinated ? 'coordinated' : ''} ${isToday ? 'today' : ''} ${isHighlighted ? 'highlight' : ''}`}
         onClick={() => onClick(event)}
      >
         <div className="event-header">
            <h4 className="event-title">{event.title}</h4>
            <div className="event-badges">
               {isToday && <span className="today-badge">오늘</span>}
               {event.isCoordinated && (
                  <span className="coordinated-badge">확정</span>
               )}
            </div>
         </div>
         {event.isCoordinated && event.roomName && (
            <p className="event-room">📅 {event.roomName}</p>
         )}
         <div className="event-info">
            <p className="event-date">{event.date}</p>
            <p className="event-time">{event.time} ~ {event.endTime}</p>
            {event.location && (
               <p className="event-location">📍 {event.location}</p>
            )}
            <p className="event-participants">👥 {event.participants}명</p>
         </div>
         {event.note && (
            <div style={{ marginTop: '4px', padding: '3px 8px', backgroundColor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px', fontSize: '12px', color: '#92400e' }}>
               📝 {event.note}
            </div>
         )}
      </div>
   );
};

const MobileScheduleView = ({ user, isVoiceEnabled, setIsVoiceEnabled }) => {
   const navigate = useNavigate();
   const { showToast } = useToast();
   const [isSidebarOpen, setIsSidebarOpen] = useState(false);
   const [selectedEvent, setSelectedEvent] = useState(null);
   const [showMapModal, setShowMapModal] = useState(false); // 지도 모달 표시 여부
   const [selectedLocation, setSelectedLocation] = useState(null); // 선택된 장소
   const [highlightToday, setHighlightToday] = useState(false); // 오늘 일정 하이라이트
   const [viewMode, setViewMode] = useState('day'); // 'day' | 'week' | 'month'
   const [navDate, setNavDate] = useState(() => new Date()); // 주/월 탐색 기준 날짜

   // 데이터 상태
   const [globalEvents, setGlobalEvents] = useState([]);
   const [personalTimes, setPersonalTimes] = useState([]);
   const [dataLoaded, setDataLoaded] = useState(false);

   // 오늘 일정으로 스크롤하기 위한 ref
   const todayRef = useRef(null);
   // 스케줄 컨텐츠 스크롤 컨테이너 ref
   const contentRef = useRef(null);


   // user를 ref로 관리하여 useCallback 안정성 확보
   const userRef = useRef(user);
   userRef.current = user;

   // 1. API 호출 함수 정의
   // 나의 일정 가져오기
   const fetchEvents = useCallback(async () => {
      try {
         // 모든 사용자: DB에서 이벤트 가져오기
         const currentUser = auth.currentUser;
         if (!currentUser) return;
         const response = await fetch(`${API_BASE_URL}/api/events`, {
            headers: { 'Authorization': `Bearer ${await currentUser.getIdToken()}` },
         });
         if (!response.ok) throw new Error('Failed to fetch events');
         const data = await response.json();
         const formattedEvents = data.events.map(event => {
            const start = event.startTime ? new Date(event.startTime) : null;
            const end = event.endTime ? new Date(event.endTime) : null;
            return {
               id: event._id,
               title: event.title,
               date: start ? start.toISOString().split('T')[0] : '',
               time: start ? start.toTimeString().substring(0, 5) : '',
               endTime: end ? end.toTimeString().substring(0, 5) : '',
               participants: Array.isArray(event.participants) ? event.participants.length : (event.participants || 1),
               priority: event.priority || 3,
               color: event.color || 'blue',
               location: event.location || null
            };
         });
         setGlobalEvents(formattedEvents);
      } catch (error) {
         console.error('Fetch events error:', error);
      }
   }, [API_BASE_URL]);

   // 개인시간 (확정된 일정) 가져오기
   const fetchPersonalTimes = useCallback(async () => {
      try {
         // 모든 사용자: DB에서 personalTimes 가져오기
         const currentUser = auth.currentUser;
         if (!currentUser) return;

         const response = await fetch(`${API_BASE_URL}/api/users/profile/schedule`, {
            headers: { 'Authorization': `Bearer ${await currentUser.getIdToken()}` },
         });
         if (!response.ok) throw new Error('Failed to fetch personal times');

         const data = await response.json();

         const personalTimesArray = data.personalTimes || [];
         const mergedPersonalTimes = [];
         const processedIds = new Set();

         const byDate = {};
         personalTimesArray.forEach(pt => {
            if (!pt.specificDate) return;
            if (!byDate[pt.specificDate]) byDate[pt.specificDate] = [];
            byDate[pt.specificDate].push(pt);
         });

         Object.keys(byDate).forEach(date => {
            const dayEvents = byDate[date].sort((a, b) => a.startTime.localeCompare(b.startTime));

            dayEvents.forEach((pt, idx) => {
               if (processedIds.has(pt.id)) return;

               // 🆕 구글 이벤트인 경우 별도 처리 (pt- 접두사 붙이지 않음)
               if (pt.isGoogleEvent) {
                  mergedPersonalTimes.push({
                     id: pt.googleEventId || pt.id,
                     googleEventId: pt.googleEventId || pt.id,
                     title: pt.title || '일정',
                     date: pt.specificDate,
                     time: pt.startTime,
                     endTime: pt.endTime,
                     participants: pt.participants || 1,
                     priority: 3,
                     color: '#3b82f6',  // 모든 구글 일정 파란색
                     isCoordinated: pt.isCoordinationConfirmed || false,  // 🆕 확정 여부
                     isGoogleEvent: true,
                     suggestionId: pt.suggestionId || null,
                     roomId: pt.roomId || null,
                     location: pt.location || null,
                     participantNames: pt.participantNames || [],
                     totalMembers: pt.totalMembers || 0,
                     externalParticipants: pt.externalParticipants || []
                  });
                  processedIds.add(pt.id);
                  return;
               }

               if (pt.title && pt.title.includes('이동시간')) {
                  const nextEvent = dayEvents[idx + 1];
                  if (nextEvent &&
                      nextEvent.startTime === pt.endTime &&
                      pt.title.split('-')[0].trim() === nextEvent.title.split('-')[0].trim()) {
                     mergedPersonalTimes.push({
                        id: `pt-${nextEvent.id}`,
                        title: nextEvent.title,
                        date: nextEvent.specificDate,
                        time: pt.startTime,
                        endTime: nextEvent.endTime,
                        participants: pt.participants || nextEvent.participants || 1,
                        priority: 3,
                        color: nextEvent.color || '#3B82F6',
                        isCoordinated: true,
                        roomName: nextEvent.title.split('-')[0].trim(),
                        location: pt.location || nextEvent.location || null,
                        locationLat: pt.locationLat || nextEvent.locationLat || null,
                        locationLng: pt.locationLng || nextEvent.locationLng || null,
                        transportMode: nextEvent.transportMode || pt.transportMode || null,
                        hasTravelTime: true,
                        travelStartTime: pt.startTime,
                        travelEndTime: pt.endTime,
                        roomId: pt.roomId || nextEvent.roomId || null,
                        suggestionId: pt.suggestionId || nextEvent.suggestionId || null,
                        participantNames: nextEvent.participantNames || pt.participantNames || [],
                        totalMembers: nextEvent.totalMembers || pt.totalMembers || 0,
                        externalParticipants: nextEvent.externalParticipants || pt.externalParticipants || []
                     });
                     processedIds.add(pt.id);
                     processedIds.add(nextEvent.id);
                  } else {
                     mergedPersonalTimes.push({
                        id: `pt-${pt.id}`, title: pt.title, date: pt.specificDate,
                        time: pt.startTime, endTime: pt.endTime, participants: pt.participants || 1,
                        priority: 3, color: pt.color || '#FFA500',
                        isCoordinated: !!(pt.suggestionId || (pt.title && pt.title.includes('-'))),
                        roomName: pt.title && pt.title.includes('-') ? pt.title.split('-')[0].trim() : undefined,
                        location: pt.location || null, locationLat: pt.locationLat || null,
                        locationLng: pt.locationLng || null, transportMode: pt.transportMode || null,
                        roomId: pt.roomId || null,
                        suggestionId: pt.suggestionId || null,
                        participantNames: pt.participantNames || [], totalMembers: pt.totalMembers || 0,
                        externalParticipants: pt.externalParticipants || [],
                        note: pt.note || null
                     });
                     processedIds.add(pt.id);
                  }
               } else {
                  mergedPersonalTimes.push({
                     id: `pt-${pt.id}`, title: pt.title || '개인 일정', date: pt.specificDate,
                     time: pt.startTime, endTime: pt.endTime, participants: pt.participants || 1,
                     priority: 3, color: pt.color || '#10B981',
                     isCoordinated: !!(pt.suggestionId || (pt.title && pt.title.includes('-'))),
                     roomName: pt.title && pt.title.includes('-') ? pt.title.split('-')[0].trim() : undefined,
                     location: pt.location || null, locationLat: pt.locationLat || null,
                     locationLng: pt.locationLng || null, transportMode: pt.transportMode || null,
                     hasTravelTime: pt.hasTravelTime || false,
                     roomId: pt.roomId || null,
                     suggestionId: pt.suggestionId || null,
                     participantNames: pt.participantNames || [], totalMembers: pt.totalMembers || 0,
                     externalParticipants: pt.externalParticipants || [],
                     note: pt.note || null
                  });
                  processedIds.add(pt.id);
               }
            });
         });

         setPersonalTimes(mergedPersonalTimes);
      } catch (error) {
         console.error('Fetch personal times error:', error);
      }
   }, [API_BASE_URL]);

   // 새로고침 핸들러
   const handleRefresh = useCallback(async () => {
      try {
         await Promise.all([fetchEvents(), fetchPersonalTimes()]);
         // 스크롤 컨테이너 맨 위로 이동
         if (contentRef.current) {
            contentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
         }
         showToast('새로고침 완료');
      } catch (error) {
         showToast('새로고침에 실패했습니다.');
      }
   }, [fetchEvents, fetchPersonalTimes, showToast]);

   // 2. 데이터 로드 Effect
   useEffect(() => {
      const loadData = async () => {
         await Promise.all([fetchEvents(), fetchPersonalTimes()]);
         setDataLoaded(true);
      };
      loadData();

      // 다른 페이지에서 일정 변경 후 돌아올 때 자동 갱신
      const handleCalendarUpdate = () => loadData();
      window.addEventListener('calendarUpdate', handleCalendarUpdate);
      const handleVisibility = () => {
         if (document.visibilityState === 'visible') loadData();
      };
      document.addEventListener('visibilitychange', handleVisibility);

      return () => {
         window.removeEventListener('calendarUpdate', handleCalendarUpdate);
         document.removeEventListener('visibilitychange', handleVisibility);
      };
   }, [fetchEvents, fetchPersonalTimes]);

   // 3. useMemo (전체 일정 시간순 정렬 + 가장 가까운 일정 인덱스)
   const { allEvents, todayStr, closestEventIndex } = useMemo(() => {
      const today = new Date();
      const todayStr = new Date(today.getTime() - (today.getTimezoneOffset() * 60000)).toISOString().split('T')[0];

      const allEvents = [...globalEvents, ...personalTimes];

      // 시간순 정렬 함수
      const sortByDateTime = (a, b) => {
         const dateCompare = a.date.localeCompare(b.date);
         if (dateCompare !== 0) return dateCompare;
         const timeA = a.time || '00:00';
         const timeB = b.time || '00:00';
         return timeA.localeCompare(timeB);
      };

      const sorted = allEvents.sort(sortByDateTime);

      // 오늘 첫 일정 인덱스, 없으면 가장 가까운 미래 일정 인덱스
      let closestIdx = -1;
      const todayIdx = sorted.findIndex(e => e.date === todayStr);
      if (todayIdx !== -1) {
         closestIdx = todayIdx;
      } else {
         closestIdx = sorted.findIndex(e => e.date > todayStr);
      }

      return {
         allEvents: sorted,
         todayStr,
         closestEventIndex: closestIdx
      };
   }, [globalEvents, personalTimes]);

   // 3-1. 데이터 로드 후 가장 가까운 일정으로 자동 스크롤
   useEffect(() => {
      if (dataLoaded && todayRef.current) {
         // 약간의 딜레이로 DOM 렌더링 완료 후 스크롤
         const timer = setTimeout(() => {
            todayRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
         }, 300);
         return () => clearTimeout(timer);
      }
   }, [dataLoaded]);

   // 4. 핸들러 함수들
   // 일정 클릭 핸들러
   const handleEventClick = (event) => {
      setSelectedEvent(event);
   };

   // 이전 일정의 위치 찾기
   const getPreviousEventLocation = useCallback((currentEvent) => {
      if (!currentEvent) return null;

      // 현재 이벤트의 인덱스 찾기
      const currentIndex = allEvents.findIndex(e => e.id === currentEvent.id);

      // 이전 이벤트가 있으면 그 위치 반환
      if (currentIndex > 0) {
         const prevEvent = allEvents[currentIndex - 1];
         // 같은 날짜인지 확인
         if (prevEvent.date === currentEvent.date) {
             // 이전 일정의 목적지 (location) 확인
             if (prevEvent.location && prevEvent.locationLat && prevEvent.locationLng) {
                 return {
                     address: prevEvent.location,
                     lat: prevEvent.locationLat,
                     lng: prevEvent.locationLng,
                     name: prevEvent.location // 장소 이름
                 };
             }
         }
      }
      return null;
   }, [allEvents]);

   // 오늘 버튼 클릭 핸들러
   const handleScrollToToday = () => {
      setNavDate(new Date()); // 주/월 모드에서도 오늘로 이동
      if (viewMode === 'day' && todayRef.current) {
         todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
         setHighlightToday(true);
         setTimeout(() => setHighlightToday(false), 3000);
      }
   };

   // 날짜 라벨 생성 (카톡 스타일)
   const getDateLabel = (dateStr) => {
      const today = new Date();
      const todayStr = new Date(today.getTime() - (today.getTimezoneOffset() * 60000)).toISOString().split('T')[0];

      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = new Date(yesterday.getTime() - (yesterday.getTimezoneOffset() * 60000)).toISOString().split('T')[0];

      if (dateStr === todayStr) {
         return '오늘';
      } else if (dateStr === yesterdayStr) {
         return '어제';
      } else {
         const [year, month, day] = dateStr.split('-');
         const date = new Date(dateStr);
         const weekdays = ['일', '월', '화', '수', '목', '금', '토'];
         const weekday = weekdays[date.getDay()];
         return `${year}년 ${parseInt(month)}월 ${parseInt(day)}일 ${weekday}요일`;
      }
   };

   // ── 뷰 모드 헬퍼 ──────────────────────────────────────────────
   const toDateStr = (d) => {
      const y  = d.getFullYear();
      const m  = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
   };

   const getWeekRange = (d) => {
      const start = new Date(d);
      start.setDate(d.getDate() - d.getDay()); // 일요일
      start.setHours(0, 0, 0, 0);
      const end = new Date(start);
      end.setDate(start.getDate() + 6); // 토요일
      return { start, end };
   };

   const getNavLabel = useCallback(() => {
      if (viewMode === 'week') {
         const { start, end } = getWeekRange(navDate);
         const y  = navDate.getFullYear();
         const sM = start.getMonth() + 1;
         const sD = start.getDate();
         const eM = end.getMonth() + 1;
         const eD = end.getDate();
         if (sM === eM) return `${y}년 ${sM}월 ${sD}일 ~ ${eD}일`;
         return `${y}년 ${sM}월 ${sD}일 ~ ${eM}월 ${eD}일`;
      }
      if (viewMode === 'month') {
         return `${navDate.getFullYear()}년 ${navDate.getMonth() + 1}월`;
      }
      return '';
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [viewMode, navDate]);

   const handlePrev = () => {
      setNavDate(d => {
         const nd = new Date(d);
         if (viewMode === 'week') nd.setDate(nd.getDate() - 7);
         else nd.setMonth(nd.getMonth() - 1);
         return nd;
      });
   };

   const handleNext = () => {
      setNavDate(d => {
         const nd = new Date(d);
         if (viewMode === 'week') nd.setDate(nd.getDate() + 7);
         else nd.setMonth(nd.getMonth() + 1);
         return nd;
      });
   };

   // 뷰 모드에 따라 필터된 이벤트
   const displayEvents = useMemo(() => {
      if (viewMode === 'day') return allEvents;

      if (viewMode === 'week') {
         const { start, end } = getWeekRange(navDate);
         const startStr = toDateStr(start);
         const endStr   = toDateStr(end);
         return allEvents.filter(e => e.date >= startStr && e.date <= endStr);
      }

      if (viewMode === 'month') {
         const y = navDate.getFullYear();
         const m = String(navDate.getMonth() + 1).padStart(2, '0');
         return allEvents.filter(e => e.date.startsWith(`${y}-${m}`));
      }

      return allEvents;
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [allEvents, viewMode, navDate]);

   // 표시 목록 기준 가장 가까운 오늘/미래 인덱스
   const displayClosestIndex = useMemo(() => {
      const todayIdx = displayEvents.findIndex(e => e.date === todayStr);
      if (todayIdx !== -1) return todayIdx;
      return displayEvents.findIndex(e => e.date > todayStr);
   }, [displayEvents, todayStr]);

   // 모달 닫기
   const handleCloseModal = () => {
      setSelectedEvent(null);
   };

   // 지도 모달 열기 — roomId가 있으면 조원 위치 API 호출, 없으면 타이틀 기반 폴백
   const handleOpenMap = async (address, lat, lng, eventContext) => {
      let participantLocations = [];
      const roomId = eventContext?.roomId;
      const date = eventContext?.date;
      const eventTitle = eventContext?.title;

      try {
         const currentUser = auth.currentUser;
         if (currentUser) {
            const token = await currentUser.getIdToken();
            const todayStr = new Date().toISOString().split('T')[0];
            const isFuture = date && date > todayStr;
            let gpsStr = '';

            if (!isFuture) {
               // 오늘 약속: GPS 수집
               try {
                  await new Promise(resolve => {
                     navigator.geolocation.getCurrentPosition(
                        pos => { gpsStr = `&lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`; resolve(); },
                        () => resolve(),
                        { maximumAge: 60000, timeout: 2000 }
                     );
                  });
               } catch (_) {}
            }

            const dateStr = date ? `?forDate=${date}` : '';
            // gpsStr starts with '&'; if dateStr is empty, replace '&' with '?' to avoid broken URL
            const gpsQuery = gpsStr && !dateStr ? gpsStr.replace('&', '?') : gpsStr;
            let endpoint = null;

            if (roomId) {
               // roomId 있는 경우: 정확한 조회
               endpoint = `${API_BASE_URL}/api/engine/room-members/${roomId}${dateStr}${gpsQuery}`;
            } else if (eventTitle && eventTitle.includes(' - ')) {
               // roomId 없는 기존 이벤트: 타이틀 기반 폴백 (title= 이후 & 접두사 유지)
               endpoint = `${API_BASE_URL}/api/engine/room-members-by-title?title=${encodeURIComponent(eventTitle)}${dateStr ? '&' + dateStr.slice(1) : ''}${gpsStr}`;
            }

            if (endpoint) {
               const res = await fetch(endpoint, { headers: { Authorization: `Bearer ${token}` } });
               const data = await res.json();
               if (data.success && Array.isArray(data.locations)) participantLocations = data.locations;
            }
         }
      } catch (_) {}

      setSelectedLocation({ address, lat, lng, participantLocations });
      setShowMapModal(true);
   };

   // 일정 삭제 핸들러
   const handleDeleteEvent = async (event) => {
      try {
         const currentUser = auth.currentUser;
         if (!currentUser) return;
         const token = await currentUser.getIdToken();

         // pt- 접두사가 있으면 personalTime 삭제를 우선 (서버에서 구글캘린더도 같이 처리)
         if (event.id && event.id.startsWith('pt-')) {
            const personalTimeId = event.id.replace('pt-', '');
            const response = await fetch(`${API_BASE_URL}/api/users/profile/schedule/${personalTimeId}`, {
               method: 'DELETE',
               headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) {
               // DB에 없는 구글 캘린더 이벤트 → 구글에서 직접 삭제
               if (event.isGoogleEvent && event.googleEventId) {
                  await googleCalendarService.deleteEvent(event.googleEventId);
               } else {
                  throw new Error('Failed to delete personal time');
               }
            }
         } else if (event.isGoogleEvent) {
            // 순수 구글 캘린더 이벤트 (pt- 없음)
            if (event.suggestionId) {
               const response = await fetch(`${API_BASE_URL}/api/users/profile/schedule/google/${event.suggestionId}`, {
                  method: 'DELETE',
                  headers: { 'Authorization': `Bearer ${token}` },
               });
               if (!response.ok) {
                  // 서버에 없으면 구글 캘린더에서 직접 삭제
                  const googleEventId = event.googleEventId || event.id;
                  await googleCalendarService.deleteEvent(googleEventId);
               }
            } else {
               const googleEventId = event.googleEventId || event.id;
               await googleCalendarService.deleteEvent(googleEventId);
            }
         } else {
            // Global Event 삭제
            const response = await fetch(`${API_BASE_URL}/api/events/${event.id}`, {
               method: 'DELETE',
               headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to delete event');
         }

         // 삭제 성공 시 모달 닫고 데이터 새로고침
         setSelectedEvent(null);
         await Promise.all([fetchEvents(), fetchPersonalTimes()]);
      } catch (error) {
         console.error('Delete event error:', error);
         showToast('일정 삭제에 실패했습니다.');
      }
   };

      // 지도 모달 닫기
   const handleCloseMapModal = () => {
      setShowMapModal(false);
      setSelectedLocation(null);
   };

   const handleLogout = async () => {
      try {
         await auth.signOut();
         localStorage.removeItem('loginMethod');
         navigate('/auth');
      } catch (error) {
         console.error('Logout error:', error);
      }
   };

   return (
      <div className="mobile-schedule-view">
         {/* 사이드바 오버레이 */}
         {isSidebarOpen && (
            <div className="sidebar-overlay" onClick={() => setIsSidebarOpen(false)}></div>
         )}

         {/* 사이드바 */}
         <nav className={`mobile-sidebar ${isSidebarOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
               <h2 className="sidebar-title">메뉴</h2>
               <button className="sidebar-close-btn" onClick={() => setIsSidebarOpen(false)}>✕</button>
            </div>
            <div className="sidebar-menu">
               <button className="sidebar-item" onClick={() => navigate('/')}>
                  🏠 홈으로
               </button>
               <button className="sidebar-item" onClick={() => navigate('/schedule')}>
                  📅 내 일정
               </button>
               <button className="sidebar-item" onClick={() => navigate('/groups')}>
                  👥 그룹
               </button>
               <button className="sidebar-item" onClick={() => navigate('/calendar')}>
                  📆 달력
               </button>
               <button className="sidebar-item" onClick={() => navigate('/settings')}>
                  ⚙️ 설정
               </button>
            </div>
         </nav>

         {/* 모바일 헤더 */}
         <header className="mobile-header">
            <div className="mobile-header-content">
               {/* 왼쪽: 햄버거 메뉴 + 로고 */}
               <div className="mobile-header-left">
                  <button className="mobile-menu-btn" onClick={() => setIsSidebarOpen(true)}>
                     <Menu size={24} />
                  </button>
                  <div className="mobile-logo-btn" onClick={() => navigate('/')}>
                     <div className="mobile-logo-wrapper">
                        <img src="/heyheylogo.png" alt="MeetAgent Logo" className="mobile-logo-img" />
                        <div className={`mobile-login-indicator ${user?.google?.refreshToken ? 'google' : 'local'}`}></div>
                     </div>
                     <h1 className="mobile-logo-text">MeetAgent</h1>
                  </div>
               </div>

               {/* 오른쪽: 버튼들 */}
               <div className="mobile-header-right">
                  {/* 프로필 버튼 */}
                  <button className="mobile-profile-btn" onClick={() => navigate('/settings')} title="설정">
                     {user && user.firstName ? user.firstName : <User size={18} />}
                  </button>

                  {/* 로그아웃 버튼 */}
                  <button
                     className="mobile-logout-btn"
                     onClick={handleLogout}
                     title="로그아웃">
                     <LogOut size={16} />
                  </button>
               </div>
            </div>
         </header>

         {/* 상단 헤더: 전체 일정 + 오늘 버튼(일 모드) + 뷰 토글 */}
         <div className="schedule-header">
            <h2 className="schedule-title">전체 일정</h2>
            <div className="schedule-header-right">
               {viewMode === 'day' && (
                  <button className="today-btn" onClick={handleScrollToToday}>
                     <Calendar size={16} />
                     오늘
                  </button>
               )}
               <div className="view-toggle">
                  <button
                     className={`view-toggle-btn${viewMode === 'day' ? ' active' : ''}`}
                     onClick={() => setViewMode('day')}>일</button>
                  <button
                     className={`view-toggle-btn${viewMode === 'week' ? ' active' : ''}`}
                     onClick={() => setViewMode('week')}>주</button>
                  <button
                     className={`view-toggle-btn${viewMode === 'month' ? ' active' : ''}`}
                     onClick={() => setViewMode('month')}>월</button>
               </div>
            </div>
         </div>

         {/* 주/월 탐색 바 */}
         {viewMode !== 'day' && (
            <div className="period-nav">
               <button className="period-nav-btn" onClick={handlePrev}>‹</button>
               <span className="period-nav-label">{getNavLabel()}</span>
               <button className="period-nav-btn" onClick={handleNext}>›</button>
            </div>
         )}

         {/* 컨텐츠 영역 */}
         <div className="schedule-content" ref={contentRef}>
            {!dataLoaded ? (
               <div className="tab-content loading">로딩 중...</div>
            ) : displayEvents.length === 0 ? (
               <p className="empty-message">
                  {viewMode === 'day' ? '일정이 없습니다.' :
                   viewMode === 'week' ? '이번 주 일정이 없습니다.' :
                   '이번 달 일정이 없습니다.'}
               </p>
            ) : (
               <div className="event-list">
                  {displayEvents.map((event, index) => {
                     const isToday = event.date === todayStr;
                     const isClosest = index === displayClosestIndex;

                     // 이전 이벤트와 날짜가 다르면 날짜 구분선 표시
                     const prevEvent = index > 0 ? displayEvents[index - 1] : null;
                     const showDateDivider = !prevEvent || prevEvent.date !== event.date;

                     return (
                        <React.Fragment key={event.id}>
                           {showDateDivider && (
                              <div className="date-divider">
                                 <div className="date-divider-line"></div>
                                 <span className="date-divider-label">{getDateLabel(event.date)}</span>
                                 <div className="date-divider-line"></div>
                              </div>
                           )}
                           <EventCard
                              event={event}
                              onClick={handleEventClick}
                              isToday={isToday}
                              isHighlighted={isToday && highlightToday}
                              cardRef={isClosest ? todayRef : null}
                           />
                        </React.Fragment>
                     );
                  })}
               </div>
            )}
         </div>

         {/* 일정 상세 모달 */}
         {selectedEvent && (
            <EventDetailModal
               event={selectedEvent}
               user={user}
               onClose={handleCloseModal}
               onOpenMap={handleOpenMap}
               onDelete={handleDeleteEvent}
               previousLocation={getPreviousEventLocation(selectedEvent)}
            />
         )}

         {/* 지도 모달 */}
         {showMapModal && selectedLocation && (
            <MapModal
               address={selectedLocation.address}
               lat={selectedLocation.lat}
               lng={selectedLocation.lng}
               onClose={handleCloseMapModal}
               participantLocations={selectedLocation.participantLocations || []}
            />
         )}

         {/* 하단 네비게이션 바 */}
         <BottomNavigation
            onRefresh={handleRefresh}
         />

      </div>
   );
};

export default MobileScheduleView;
